// =====================================================
// components/TikTokVideoModal.tsx
// =====================================================
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Icônes, couleurs, IDs, structure de données restent codés en dur, inchangés.

import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet, View, Text, Modal, TouchableOpacity,
  FlatList, ScrollView, StatusBar, Platform, Dimensions,
} from 'react-native';
import { Ionicons }    from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { VideoView, useVideoPlayer } from 'expo-video';
import * as Haptics    from 'expo-haptics';
import { useTranslation } from 'react-i18next';
import { SafeImage }   from './MediaViewers';
import { VideoFeedItem, GameType, NATIVE, getInitials } from '../lib/feedHelpers';

const { width, height } = Dimensions.get('window');
const TIK_CLOSE_TOP = Platform.OS === 'ios' ? 54 : 30;

interface TikTokVideoModalProps {
  visible:    boolean;
  items:      VideoFeedItem[];
  startIndex: number;
  onClose:    () => void;
  onVote:     (gameType: GameType, runId: string, voteForUserId: string, sessionId: string, currentlyVoted: boolean) => Promise<void>;
}

export default function TikTokVideoModal({ visible, items, startIndex, onClose, onVote }: TikTokVideoModalProps) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const flatRef      = useRef<FlatList>(null);
  const webScrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    if (!visible || items.length === 0) return;
    setCurrentIndex(startIndex);
    if (Platform.OS === 'web') {
      setTimeout(() => { webScrollRef.current?.scrollTo({ y: startIndex * height, animated: false }); }, 50);
    } else {
      flatRef.current?.scrollToIndex({ index: startIndex, animated: false });
    }
  }, [visible, startIndex]);

  if (!visible || items.length === 0) return null;

  return (
    <Modal visible={visible} animationType="slide" statusBarTranslucent onRequestClose={onClose}>
      <StatusBar hidden />
      <View style={styles.root}>
        <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
          <Ionicons name="close" size={28} color="#FFF" />
        </TouchableOpacity>
        {Platform.OS === 'web' ? (
          <ScrollView
            ref={webScrollRef} style={{ flex: 1 }}
            pagingEnabled showsVerticalScrollIndicator={false} scrollEventThrottle={1}
            onScroll={e => {
              const idx = Math.round(e.nativeEvent.contentOffset.y / height);
              if (idx !== currentIndex) setCurrentIndex(idx);
            }}
          >
            {items.map((item, index) => (
              <View key={item.mediaId} style={{ width, height }}>
                <TikTokVideoItem item={item} isActive={index === currentIndex} onVote={onVote} />
              </View>
            ))}
          </ScrollView>
        ) : (
          <FlatList
            ref={flatRef} data={items} keyExtractor={item => item.mediaId}
            pagingEnabled showsVerticalScrollIndicator={false}
            snapToInterval={height} decelerationRate="fast"
            getItemLayout={(_, index) => ({ length: height, offset: height * index, index })}
            initialScrollIndex={startIndex}
            onMomentumScrollEnd={e => {
              const idx = Math.round(e.nativeEvent.contentOffset.y / height);
              setCurrentIndex(idx);
            }}
            renderItem={({ item, index }) => (
              <TikTokVideoItem item={item} isActive={index === currentIndex} onVote={onVote} />
            )}
          />
        )}
      </View>
    </Modal>
  );
}

function TikTokVideoItem({ item, isActive, onVote }: {
  item: VideoFeedItem; isActive: boolean;
  onVote: TikTokVideoModalProps['onVote'];
}) {
  const { t } = useTranslation();
  const [totalVotes, setTotalVotes] = useState(item.total_votes);
  const [userVoted,  setUserVoted]  = useState(item.user_voted);
  const [isVoting,   setIsVoting]   = useState(false);
  const [hasError,   setHasError]   = useState(false);

  const player = useVideoPlayer({ uri: item.videoUrl }, p => {
    p.loop  = true;
    p.muted = false;
    if (isActive) p.play();
  });

  useEffect(() => { setTotalVotes(item.total_votes); setUserVoted(item.user_voted); }, [item]);
  useEffect(() => {
    if (hasError) return;
    try {
      if (isActive) player.play();
      else          player.pause();
    } catch {}
  }, [isActive, hasError]);

  const handleVoteLocal = async () => {
    if (isVoting) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setIsVoting(true);
    const n = !userVoted; setUserVoted(n); setTotalVotes(p => n ? p + 1 : p - 1);
    try { await onVote(item.game_type, item.run_id, item.user_id, item.session_id, userVoted); }
    catch { setUserVoted(!n); setTotalVotes(p => n ? p - 1 : p + 1); }
    finally { setIsVoting(false); }
  };

  const authorPrenom = item.author?.prenom || '';
  const authorNom    = item.author?.nom    || '';

  return (
    <View style={styles.item}>
      {hasError ? (
        <View style={[StyleSheet.absoluteFill, { backgroundColor: '#111', justifyContent: 'center', alignItems: 'center' }]}>
          <Ionicons name="videocam-off-outline" size={48} color="rgba(255,255,255,0.3)" />
        </View>
      ) : (
        <VideoView player={player} style={StyleSheet.absoluteFill} contentFit="cover" nativeControls={false} onError={() => setHasError(true)} />
      )}
      <LinearGradient colors={['transparent', 'rgba(0,0,0,0.75)']} style={styles.gradient} />
      <View style={styles.infoRow}>
        <SafeImage
          uri={item.author?.avatar_url}
          style={styles.avatar}
          resizeMode="cover"
          fallback={
            <View style={[styles.avatar, styles.avatarFallback]}>
              <Text style={styles.avatarText}>{authorPrenom.charAt(0)}{authorNom.charAt(0)}</Text>
            </View>
          }
        />
        <View style={styles.infoText}>
          <Text style={styles.authorName}>{authorPrenom} {authorNom}</Text>
          <Text style={styles.runLabel}>{t('tikTokVideoModal.runLabel', { number: item.run_number, title: item.run_title })}</Text>
          {item.description ? <Text style={styles.desc} numberOfLines={2}>{item.description}</Text> : null}
        </View>
      </View>
      <TouchableOpacity
        style={[styles.voteBtn, userVoted && styles.voteBtnVoted]}
        onPress={handleVoteLocal}
        disabled={isVoting}
        activeOpacity={0.85}
      >
        <Ionicons name={userVoted ? 'heart' : 'heart-outline'} size={26} color={userVoted ? '#FF4D6A' : '#FFF'} />
        <Text style={styles.voteCount}>{totalVotes}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  root:         { flex: 1, backgroundColor: '#000' },
  closeBtn:     { position: 'absolute', top: TIK_CLOSE_TOP, right: 16, zIndex: 100, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 20, width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  item:         { width, height, backgroundColor: '#000' },
  gradient:     { position: 'absolute', bottom: 0, left: 0, right: 0, height: 220 },
  infoRow:      { position: 'absolute', bottom: 60, left: 16, right: 80, flexDirection: 'row', alignItems: 'flex-end', gap: 12 },
  avatar:       { width: 44, height: 44, borderRadius: 22, borderWidth: 2, borderColor: '#FFF' },
  avatarFallback:{ backgroundColor: '#E65100', alignItems: 'center', justifyContent: 'center' },
  avatarText:   { color: '#FFF', fontWeight: '800', fontSize: 14 },
  infoText:     { flex: 1, gap: 3 },
  authorName:   { color: '#FFF', fontWeight: '800', fontSize: 15 },
  runLabel:     { color: 'rgba(255,255,255,0.7)', fontSize: 12 },
  desc:         { color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 18, marginTop: 2 },
  voteBtn:      { position: 'absolute', right: 16, bottom: 80, alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: 30, paddingHorizontal: 10, paddingVertical: 12 },
  voteBtnVoted: { backgroundColor: 'rgba(255,77,106,0.2)' },
  voteCount:    { color: '#FFF', fontSize: 13, fontWeight: '800' },
});
